import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTS = [
  { v: 'pending', l: 'Pending' },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'client_dependency', l: 'Client Dependency' },
  { v: 'blocked', l: 'Blocked' },
  { v: 'completed', l: 'Completed' },
];
const PRIORITY_OPTS = [
  { v: 'low', l: 'Low' },
  { v: 'medium', l: 'Medium' },
  { v: 'high', l: 'High' },
  { v: 'urgent', l: 'Urgent' },
];
const STATUS_COLOR = {
  pending: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  client_dependency: 'bg-amber-100 text-amber-700',
  blocked: 'bg-rose-100 text-rose-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const emptyNewTask = () => ({
  project_id: '', title: '', description: '',
  priority: 'medium', status: 'pending', due_date: '', estimated_hours: 0,
});

export default function MyTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expanded, setExpanded] = useState(null);  // task id
  const today = new Date().toISOString().slice(0, 10);
  // staff's daily update payloads, keyed by task_id
  const [updates, setUpdates] = useState({});
  // history shown for an expanded task
  const [history, setHistory] = useState([]);
  const [now, setNow] = useState(Date.now());
  // Create-task modal state
  const [showNew, setShowNew] = useState(false);
  const [newTask, setNewTask] = useState(emptyNewTask());
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [tr, pr] = await Promise.all([
      axios.get(`${API_URL}/api/my/tasks`, { withCredentials: true }),
      axios.get(`${API_URL}/api/projects`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setTasks(tr.data || []); setProjects(pr.data || []);
  };
  useEffect(() => { load(); }, []);
  // Tick once per second to update live timer display
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const projName = (id) => projects.find((p) => p.id === id)?.name || '—';
  const setU = (tid, patch) => setUpdates((s) => ({ ...s, [tid]: { ...(s[tid] || {}), ...patch } }));

  // CSRM item handlers — route status updates to the correct CSRM endpoint
  const markCsrmDone = async (t) => {
    const origin = t.csrm_origin || {};
    const endpoint = origin.kind === 'action_item' ? 'action-items' : 'tasks';
    try {
      await axios.put(`${API_URL}/api/csrm/${endpoint}/${origin.id}`, { status: 'done' }, { withCredentials: true });
      toast.success('Marked done');
      load();
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };
  const reopenCsrm = async (t) => {
    const origin = t.csrm_origin || {};
    const endpoint = origin.kind === 'action_item' ? 'action-items' : 'tasks';
    const nextStatus = origin.kind === 'action_item' ? 'open' : 'pending';
    try {
      await axios.put(`${API_URL}/api/csrm/${endpoint}/${origin.id}`, { status: nextStatus, completed_at: '' }, { withCredentials: true });
      load();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // DMO item handlers — route mark-done to the appropriate DMO endpoint
  const markDmoDone = async (t) => {
    const origin = t.dmo_origin || {};
    try {
      if (origin.kind === 'social_post') {
        await axios.put(`${API_URL}/api/dmo/social-posts/${origin.id}`, { status: 'posted' }, { withCredentials: true });
      } else if (origin.kind === 'seo_submission') {
        await axios.put(`${API_URL}/api/dmo/seo-submissions/${origin.id}`, { status: 'submitted' }, { withCredentials: true });
      }
      toast.success('Marked done');
      load();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const csrmTasks = tasks.filter((t) => t.csrm_origin);
  const dmoTasks = tasks.filter((t) => t.dmo_origin);
  const pmTasks = tasks.filter((t) => !t.csrm_origin && !t.dmo_origin);

  const expand = async (t) => {
    if (expanded === t.id) { setExpanded(null); return; }
    setExpanded(t.id);
    setU(t.id, { update_date: today, work_done: '', hours: 0, status: t.status, blockers: '' });
    const h = await axios.get(`${API_URL}/api/tasks/${t.id}/updates`, { withCredentials: true });
    setHistory(h.data || []);
  };
  const postUpdate = async (t) => {
    const u = updates[t.id] || {};
    if (!u.work_done?.trim()) return toast.error('Please describe what you did');
    try {
      await axios.post(`${API_URL}/api/tasks/${t.id}/updates`, u, { withCredentials: true });
      await load();
      const h = await axios.get(`${API_URL}/api/tasks/${t.id}/updates`, { withCredentials: true });
      setHistory(h.data || []);
      setU(t.id, { work_done: '', hours: 0, blockers: '' });
      toast.success('Update posted');
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };
  const startTimer = async (t) => { await axios.post(`${API_URL}/api/tasks/${t.id}/timer/start`, {}, { withCredentials: true }); load(); };
  const stopTimer  = async (t) => { await axios.post(`${API_URL}/api/tasks/${t.id}/timer/stop`,  {}, { withCredentials: true }); load(); };

  const toggleSubtask = async (t, sub) => {
    const newSubs = (t.subtasks || []).map((s) => s.id === sub.id ? { ...s, done: !s.done } : s);
    await axios.put(`${API_URL}/api/tasks/${t.id}`, { subtasks: newSubs }, { withCredentials: true });
    load();
  };

  const uploadFile = async (t, file) => {
    const fd = new FormData(); fd.append('file', file);
    await axios.post(`${API_URL}/api/tasks/${t.id}/attachments`, fd, {
      withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
    });
    load();
  };

  const fmtRunTime = (t) => {
    if (!t.timer_running || !t.timer_started_at) return '';
    const started = new Date(t.timer_started_at).getTime();
    const sec = Math.max(0, Math.floor((now - started) / 1000));
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const createTask = async () => {
    if (!newTask.title.trim() || !newTask.project_id) {
      return toast.error('Project and title are required');
    }
    setCreating(true);
    try {
      // Backend auto-assigns to self for non-admin/non-lead users
      await axios.post(`${API_URL}/api/tasks`, newTask, { withCredentials: true });
      toast.success('Task created');
      setShowNew(false);
      setNewTask(emptyNewTask());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setCreating(false);
  };

  return (
    <div className="space-y-3 p-6" data-testid="my-tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#1a2e4a' }}>My Tasks</h2>
          <p className="text-xs text-gray-500">Daily updates · click any task to log what you did today</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-600">Today: <strong>{today}</strong></div>
          <Button
            size="sm"
            onClick={() => { setNewTask(emptyNewTask()); setShowNew(true); }}
            disabled={projects.length === 0}
            title={projects.length === 0 ? 'You are not a member of any project yet — ask your manager to add you' : 'Create a new task'}
            data-testid="my-new-task-btn"
          >
            <Plus size={14} className="mr-1" /> New Task
          </Button>
        </div>
      </div>

      {tasks.length === 0 && (
        <Card className="p-8 text-center text-sm text-gray-500" data-testid="no-tasks">
          🎉 No tasks assigned to you right now. Reach out to your manager if this is unexpected.
        </Card>
      )}

      {/* CSRM Client Success items — assigned via /csrm worksheet */}
      {csrmTasks.length > 0 && (
        <Card className="p-3 bg-indigo-50/40 border-indigo-200" data-testid="csrm-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-bold text-indigo-900">📢 Client Success Tasks ({csrmTasks.length})</div>
              <div className="text-[11px] text-indigo-700">Communication tasks &amp; meeting action items assigned to you via CSRM.</div>
            </div>
          </div>
          <div className="space-y-2">
            {csrmTasks.map((t) => {
              const isDone = t.status === 'completed';
              const isOverdue = !isDone && t.due_date && t.due_date < today;
              return (
                <div key={t.id} className={`flex items-center gap-3 p-2 rounded border ${isDone ? 'bg-emerald-50/50 border-emerald-200' : isOverdue ? 'bg-rose-50/60 border-rose-200' : 'bg-white border-gray-200'}`} data-testid={`csrm-my-${t.csrm_origin?.id}`}>
                  <input
                    type="checkbox"
                    checked={isDone}
                    onChange={() => isDone ? reopenCsrm(t) : markCsrmDone(t)}
                    className="w-4 h-4 accent-emerald-600 cursor-pointer flex-shrink-0"
                    data-testid={`csrm-my-done-${t.csrm_origin?.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${t.csrm_origin?.kind === 'action_item' ? 'bg-purple-200 text-purple-800' : 'bg-indigo-200 text-indigo-800'}`}>
                        {t.csrm_origin?.kind === 'action_item' ? 'MEETING AI' : 'COMM TASK'}
                      </span>
                      <span className="text-[10px] font-semibold text-gray-600">{t.csrm_origin?.client_name || '—'}</span>
                      {t.due_date && <span className={`text-[10px] ${isOverdue ? 'text-rose-700 font-bold' : 'text-gray-500'}`}>📅 {t.due_date}</span>}
                      {t.priority && t.priority !== 'medium' && (
                        <span className={`text-[10px] uppercase font-bold ${t.priority === 'urgent' || t.priority === 'high' ? 'text-rose-600' : 'text-gray-500'}`}>{t.priority}</span>
                      )}
                    </div>
                    <div className={`text-sm ${isDone ? 'line-through text-gray-500' : 'font-medium text-gray-900'}`}>{t.title}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* DMO Marketing items — assigned via /marketing roster or SEO tracker */}
      {dmoTasks.length > 0 && (
        <Card className="p-3 bg-pink-50/40 border-pink-200" data-testid="dmo-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-bold text-pink-900">🎯 Marketing Tasks ({dmoTasks.length})</div>
              <div className="text-[11px] text-pink-700">Social media posts &amp; SEO submissions assigned to you.</div>
            </div>
          </div>
          <div className="space-y-2">
            {dmoTasks.map((t) => {
              const isDone = t.status === 'completed';
              const isOverdue = !isDone && t.due_date && t.due_date < today;
              const kind = t.dmo_origin?.kind;
              return (
                <div key={t.id} className={`flex items-center gap-3 p-2 rounded border ${isDone ? 'bg-emerald-50/50 border-emerald-200' : isOverdue ? 'bg-rose-50/60 border-rose-200' : 'bg-white border-gray-200'}`} data-testid={`dmo-my-${t.dmo_origin?.id}`}>
                  <input
                    type="checkbox"
                    checked={isDone}
                    onChange={() => !isDone && markDmoDone(t)}
                    className="w-4 h-4 accent-emerald-600 cursor-pointer flex-shrink-0"
                    data-testid={`dmo-my-done-${t.dmo_origin?.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${kind === 'seo_submission' ? 'bg-orange-200 text-orange-800' : 'bg-pink-200 text-pink-800'}`}>
                        {kind === 'seo_submission' ? 'SEO' : 'SOCIAL'}
                      </span>
                      <span className="text-[10px] font-semibold text-gray-600">{t.dmo_origin?.client_name || '—'}</span>
                      {t.due_date && <span className={`text-[10px] ${isOverdue ? 'text-rose-700 font-bold' : 'text-gray-500'}`}>📅 {t.due_date}</span>}
                      {t.dmo_origin?.role && <span className="text-[10px] text-gray-500">· {t.dmo_origin.role}</span>}
                    </div>
                    <div className={`text-sm ${isDone ? 'line-through text-gray-500' : 'font-medium text-gray-900'}`}>{t.title}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {pmTasks.map((t) => {
        const isOpen = expanded === t.id;
        return (
          <Card key={t.id} className="p-3" data-testid={`my-task-${t.id}`}>
            <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => expand(t)}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS_COLOR[t.status] || ''}`}>
                    {STATUS_OPTS.find((s) => s.v === t.status)?.l.toUpperCase()}
                  </span>
                  <span className="text-[10px] text-gray-500 uppercase">{(t.priority || 'medium').toUpperCase()}</span>
                  {t.due_date && <span className="text-[11px] text-gray-600">📅 {t.due_date}</span>}
                  {t.timer_running && <span className="text-[11px] font-bold text-blue-600">⏱ RUNNING {fmtRunTime(t)}</span>}
                </div>
                <div className="text-sm font-semibold mt-0.5">{t.title}</div>
                <div className="text-xs text-gray-500">{projName(t.project_id)} · est {t.estimated_hours || 0}h · actual {Number(t.actual_hours || 0).toFixed(1)}h</div>
              </div>
              <div className="text-gray-400">{isOpen ? '▲' : '▼'}</div>
            </div>

            {isOpen && (
              <div className="mt-3 pt-3 border-t space-y-3">
                {t.description && <div className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 p-2 rounded">{t.description}</div>}

                {/* Subtasks */}
                {(t.subtasks || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-bold text-gray-600 uppercase mb-1">Subtasks</div>
                    {(t.subtasks || []).map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-xs py-0.5">
                        <input type="checkbox" checked={!!s.done} onChange={() => toggleSubtask(t, s)} />
                        <span className={s.done ? 'line-through text-gray-400' : ''}>{s.title}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Timer + attachments */}
                <div className="flex items-center gap-2 flex-wrap">
                  {t.timer_running ? (
                    <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" onClick={() => stopTimer(t)} data-testid={`stop-timer-${t.id}`}>
                      ⏹ Stop timer ({fmtRunTime(t)})
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => startTimer(t)} data-testid={`start-timer-${t.id}`}>▶ Start timer</Button>
                  )}
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadFile(t, f); e.target.value = ''; } }} />
                    <span className="inline-flex items-center justify-center rounded-md text-xs font-medium h-8 px-3 border bg-background hover:bg-accent">📎 Attach file</span>
                  </label>
                  {(t.attachments || []).length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {(t.attachments || []).map((a) => (
                        <a key={a.id} href={`${API_URL}${a.url}`} target="_blank" rel="noreferrer"
                          className="text-[11px] underline text-blue-700">{a.filename}</a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Daily Update Entry */}
                <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
                  <div className="text-xs font-bold text-blue-900">📝 Add Today's Update</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Label className="text-[10px]">What did you do?</Label>
                      <textarea className="w-full border rounded px-2 py-1.5 text-xs min-h-[60px]"
                        value={(updates[t.id]?.work_done) || ''} onChange={(e) => setU(t.id, { work_done: e.target.value })}
                        placeholder="Describe work completed today…" data-testid={`update-text-${t.id}`} />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-[10px]">Hours spent</Label>
                        <Input type="number" step="0.25" value={updates[t.id]?.hours ?? 0}
                          onChange={(e) => setU(t.id, { hours: parseFloat(e.target.value) || 0 })} data-testid={`update-hours-${t.id}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">New status</Label>
                        <select className="w-full border rounded px-2 py-1.5 text-xs bg-white"
                          value={updates[t.id]?.status || t.status} onChange={(e) => setU(t.id, { status: e.target.value })}
                          data-testid={`update-status-${t.id}`}>
                          {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px]">Blockers (optional)</Label>
                    <Input value={updates[t.id]?.blockers || ''} onChange={(e) => setU(t.id, { blockers: e.target.value })}
                      placeholder="Anything blocking progress?" />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => postUpdate(t)} data-testid={`post-update-${t.id}`}>📨 Post Update</Button>
                  </div>
                </div>

                {/* History */}
                <div>
                  <div className="text-[11px] font-bold text-gray-600 uppercase mb-1">Recent Updates</div>
                  {history.length === 0 && <div className="text-xs text-gray-400">No updates yet.</div>}
                  <div className="space-y-1.5">
                    {history.map((u) => (
                      <div key={u.id} className="text-xs border-l-2 border-blue-300 pl-2 py-1">
                        <div className="flex items-center gap-2 text-[10px] text-gray-500">
                          <span>{u.update_date}</span>·
                          <span>{Number(u.hours || 0)}h</span>·
                          <span className={`px-1 rounded ${STATUS_COLOR[u.status] || ''}`}>{u.status}</span>
                        </div>
                        <div className="text-gray-800 whitespace-pre-wrap">{u.work_done}</div>
                        {u.blockers && <div className="text-[10px] text-rose-700 mt-0.5">⛔ {u.blockers}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {/* New Task Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <Card className="w-full max-w-lg bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="new-task-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: '#1a2e4a' }}>Create New Task</h3>
              <button onClick={() => setShowNew(false)} className="text-gray-400 hover:text-gray-600" data-testid="new-task-close"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Project *</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={newTask.project_id}
                  onChange={(e) => setNewTask({ ...newTask, project_id: e.target.value })} data-testid="new-task-project">
                  <option value="">— Select project —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div className="text-[10px] text-gray-500 mt-1">Only projects you're a member of are shown.</div>
              </div>
              <div>
                <Label className="text-xs">Title *</Label>
                <Input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="e.g. Build login screen" data-testid="new-task-title" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[70px]"
                  value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Optional details, links, acceptance criteria…" data-testid="new-task-desc" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Priority</Label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm bg-white" value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })} data-testid="new-task-priority">
                    {PRIORITY_OPTS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm bg-white" value={newTask.status}
                    onChange={(e) => setNewTask({ ...newTask, status: e.target.value })} data-testid="new-task-status">
                    {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Est. hours</Label>
                  <Input type="number" step="0.5" value={newTask.estimated_hours}
                    onChange={(e) => setNewTask({ ...newTask, estimated_hours: parseFloat(e.target.value) || 0 })} data-testid="new-task-hours" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Due date</Label>
                <Input type="date" value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} data-testid="new-task-due" />
              </div>
              <p className="text-[11px] text-gray-500">Task will be auto-assigned to you.</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowNew(false)} data-testid="new-task-cancel">Cancel</Button>
              <Button onClick={createTask} disabled={creating} data-testid="new-task-submit">
                {creating ? 'Creating…' : 'Create Task'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
